/**
 * RecentActivityRow — one row in the admin-review timeline
 * (Block 6.F.5 §7.2 + §7.3 + §7.4; X.3.7 Part 3 humanization).
 *
 * Renders a chronological event as a HUMAN SENTENCE (never a raw diff or
 * the "did something" fallback). Per `event_kind` × `target_type`; archive/
 * restore + note lifecycle + user_tenant role/lifecycle special-cased.
 *
 * The raw before→after values (and note metadata) are moved behind a
 * per-row "Details" expander so the headline stays a clean sentence and
 * system tokens (note_id JSON, raw column values) never render inline.
 *
 * H1 anonymity discipline (r2): the row reads `is_anonymous` (not the
 * absence of `actor_user_id`) to pick the "Someone" microcopy. This
 * decouples anonymity rendering from orphan-reference handling.
 *
 * Tap-to-detail: tapping anywhere on the row (except the ⋯ overflow
 * menu and the Details control) navigates to the target detail page.
 */

import { useId, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdminTimelineRow, UserTenantRole } from '@/lib/types';
import { ROLE_DISPLAY } from '@/lib/teamRoles';
import { ActivityRowOverflowMenu } from './ActivityRowOverflowMenu';
import { humanizeFieldValue } from '@/lib/fieldDisplay';
import { formatEndsOn } from '@/lib/aiCaps';
import { formatRecordCount } from '@/lib/format';

/**
 * Timeline-facing field labels (X.3.7 Part 3). Distinct from
 * fieldDisplay's ORG/INDIVIDUAL_FIELD_LABELS (those are the SuggestEditForm
 * picker labels). The timeline reads as a sentence — "edited Acme's memory-
 * care status" — so labels are lowercase noun phrases, not picker captions.
 *
 * Fields that are NOT a simple "{actor} edited {target}'s {label}" shape
 * (is_active, note_deleted, merged_from, role, …) are handled by the
 * sentence builder, not this map.
 */
const ORG_TIMELINE_FIELD_LABELS: Record<string, string> = {
  name: 'name',
  kind: 'type',
  category: 'category',
  address: 'address',
  phone: 'phone',
  email: 'email',
  website: 'website',
  phone_2: 'second phone',
  fax: 'fax',
  payment_types: 'payment types',
  is_recommended: 'recommended status',
  medicaid: 'Medicaid status',
  dementia_unit: 'memory-care status',
  gender_restrictions: 'gender restrictions',
  service_delivery_models: 'service delivery models',
};

const INDIVIDUAL_TIMELINE_FIELD_LABELS: Record<string, string> = {
  full_name: 'name',
  title: 'title',
  business_phone: 'business phone',
  cell_phone: 'cell phone',
  fax: 'fax',
  email: 'email',
  address: 'address',
  specialties: 'specialties',
  notes: 'notes',
  is_referral_partner: 'referral-partner status',
  needs_review: 'needs-review flag',
  last_verified_at: 'last-verified date',
};

/** snake_case → "snake case" so a raw token never reaches the screen. */
function humanizeRawField(field: string): string {
  return field.replace(/_/g, ' ').trim();
}

/**
 * Operator-facing field label for a timeline row (never a raw column name,
 * never the "did something" fallback). Known fields map to a lowercase noun
 * phrase; anything unmapped degrades to the snake_case-spaced form.
 */
function fieldLabel(row: AdminTimelineRow): string {
  const field = row.field_name ?? '';
  const map =
    row.target_type === 'individual'
      ? INDIVIDUAL_TIMELINE_FIELD_LABELS
      : ORG_TIMELINE_FIELD_LABELS;
  // Fall back to a readable noun when the field is empty/unknown so the
  // possessive sentence never trails as "{name}'s " with no noun.
  return map[field] || humanizeRawField(field) || 'details';
}

/**
 * Plain-words label for a user_tenant access role (X.3.7 Part 3). Avoids
 * surfacing the snake_case enum in the sentence; unknown roles degrade to
 * the spaced form.
 */
/**
 * COLLAPSED 2026-08-27. This was a hand-maintained copy of
 * src/lib/people.ts accessRoleLabel, carrying a comment that said "the
 * two must not drift" — which is the tell. A comment asking a human to
 * keep two lists identical is a drift that has not happened yet; by the
 * time this was written there were FOUR role-label sources in the tree
 * and three of them disagreed. Now there is one (ROLE_DISPLAY), and the
 * only thing local to this file is the timeline's own null wording.
 */
function accessRoleLabel(role: string | null | undefined): string {
  if (!role) return 'a team member';
  return role in ROLE_DISPLAY ? ROLE_DISPLAY[role as UserTenantRole] : humanizeRawField(role);
}

export interface RecentActivityRowProps {
  row: AdminTimelineRow;
  tenantSlug: string;
  /** True for a seat holding archive_providers (canArchiveProviders). */
  canArchive: boolean;
  /**
   * Whether the target resource is currently archived (is_active=false).
   * Surfaces "Restore" affordance instead of "Archive."
   *
   * Phase 1 simplification: derived from the most recent direct_edit on
   * `is_active` for this target — but that's expensive client-side.
   * Pragmatic default: NOT archived; if the row IS itself an archive
   * event we infer `true` (and conversely for restore events). Imperfect
   * for older rows whose target was archived elsewhere; surfaced as a
   * forward-scope item if operate-and-monitor surfaces a need.
   */
  isTargetArchived: boolean;
  onArchiveClick: (row: AdminTimelineRow) => void;
  onRestoreClick: (row: AdminTimelineRow) => void;
}

export function RecentActivityRow({
  row,
  tenantSlug,
  canArchive,
  isTargetArchived,
  onArchiveClick,
  onRestoreClick,
}: RecentActivityRowProps): ReactNode {
  const navigate = useNavigate();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsId = useId();
  const targetName = row.target_display ?? fallbackTargetName(row.target_type);
  const detailPath = row.target_type === 'organization'
    ? `/t/${tenantSlug}/organizations/${row.target_id}`
    : row.target_type === 'individual'
      ? `/t/${tenantSlug}/individuals/${row.target_id}`
      // A person's AI limit opens the AI limits tab, where it is set.
      : row.target_type === 'user_tenant' && row.field_name === 'ai_cap'
        ? `/t/${tenantSlug}/admin-review?tab=ai-limits`
        // Phase 1.6: user_tenant rows route back to the Team tab; no
        // per-user detail page exists in Phase 1.
        : `/t/${tenantSlug}/admin-review?tab=team`;

  function handleOpenDetail(): void {
    navigate(detailPath);
  }

  const detail = rawDetail(row);

  return (
    <div
      role="article"
      aria-label={`${describeEventForAria(row, targetName)}, ${formatRelativeTime(row.created_at)}`}
      className="flex items-start gap-3 px-3 py-3 border-b border-sage-800/[8%] hover:bg-sand cursor-pointer transition-colors"
      onClick={handleOpenDetail}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink">{renderEventBody(row, targetName)}</div>
        {detail !== null && (
          <div onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              aria-expanded={detailsOpen}
              aria-controls={detailsId}
              className="mt-1 text-xs text-ink-soft hover:text-ink rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              {detailsOpen ? 'Hide details' : 'Details'}
            </button>
            {detailsOpen && (
              <div
                id={detailsId}
                className="text-xs text-ink-soft mt-1 break-words whitespace-pre-wrap"
              >
                {detail}
              </div>
            )}
          </div>
        )}
        <div className="text-xs text-ink-soft mt-1">{formatRelativeTime(row.created_at)}</div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <ActivityRowOverflowMenu
          targetName={targetName}
          isArchived={isTargetArchived}
          // Phase 1.6 — user_tenant rows never offer archive (user
          // lifecycle is deactivate via Team tab, not archive). Gate
          // upstream of the mutation defensive guard.
          canArchive={canArchive && row.target_type !== 'user_tenant'}
          onOpenDetail={handleOpenDetail}
          onArchiveClick={() => onArchiveClick(row)}
          onRestoreClick={() => onRestoreClick(row)}
        />
      </div>
    </div>
  );
}

function fallbackTargetName(targetType: AdminTimelineRow['target_type']): string {
  // Phase 1.6 — 3-way for user_tenant target.
  switch (targetType) {
    case 'organization': return 'an organization';
    case 'individual':   return 'an individual';
    case 'user_tenant':  return 'a team member';
    // The wire type is a closed 3-union, but the runtime value crosses a
    // version boundary: the server's change_log holds many more target
    // types (work_item, invoice, ...) and a repo regression or stale
    // cached page can put one here. A closed switch returned undefined
    // and prod rendered "edited undefined's status" — degrade to a
    // readable noun instead, never a hole in the sentence.
    default: return 'a record';
  }
}

function actorLabel(row: AdminTimelineRow): string {
  // H1 (r2): branch on is_anonymous explicitly, not absence of actor_user_id.
  if (row.source === 'note' && row.is_anonymous) return 'Someone';
  if (row.actor_display_name) return row.actor_display_name;
  return 'Someone';
}

/**
 * A user_tenant row whose actor is its own target. The sign-in bootstrap
 * (auth_resolve_request, ADR-005: the actor is the session user) writes
 * these when an invite takes effect or a re-invite brings a dormant
 * membership back; an owner changing their own role is the other way one
 * arises. The sentences for both leave out a false second person.
 */
function isSelfAuthored(row: AdminTimelineRow): boolean {
  return row.actor_user_id !== null && row.actor_user_id === row.target_id;
}

/**
 * The AI limit row's value, as the cm_ai_caps repo writes it:
 * {"daily_cap":…,"weekly_cap":…,"ends_on":…}. Null when it does not read as
 * that shape, so the sentence degrades instead of showing the text.
 */
function parseAiCapValue(
  v: string | null,
): { daily: number | null; weekly: number | null; endsOn: string | null } | null {
  if (v === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(v);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const o = parsed as Record<string, unknown>;
  const cap = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
  return {
    daily: cap(o.daily_cap),
    weekly: cap(o.weekly_cap),
    endsOn: typeof o.ends_on === 'string' ? o.ends_on : null,
  };
}

/**
 * "{actor} set {target}'s AI limit to 40 a day and 200 a week through 21 Nov
 * 2026", naming only the windows that are set; "{actor} cleared {target}'s AI
 * limit" for a null new value; "their own" when the actor is the target.
 */
function aiCapSentence(row: AdminTimelineRow, actor: string, targetName: string): string {
  const whose = isSelfAuthored(row) ? 'their own' : `${targetName}'s`;
  if (row.new_value === null) return `${actor} cleared ${whose} AI limit`;
  const limit = parseAiCapValue(row.new_value);
  if (limit === null) return `${actor} set ${whose} AI limit`;
  const windows: string[] = [];
  if (limit.daily !== null) windows.push(`${formatRecordCount(limit.daily)} a day`);
  if (limit.weekly !== null) windows.push(`${formatRecordCount(limit.weekly)} a week`);
  const to = windows.length > 0 ? ` to ${windows.join(' and ')}` : '';
  const through = limit.endsOn !== null ? ` through ${formatEndsOn(limit.endsOn)}` : '';
  return `${actor} set ${whose} AI limit${to}${through}`;
}

/**
 * Human sentence per (event_kind, target_type) — X.3.7 Part 3. Every code
 * path returns a non-empty readable sentence; there is NO "did something"
 * fallback (an unknown field still humanizes to a readable label).
 */
function renderEventBody(row: AdminTimelineRow, targetName: string): ReactNode {
  const actor = actorLabel(row);

  // Resource events (target_type may be organization or individual;
  // new_value carries the resolved resource name).
  if (row.event_kind === 'resource_propose') {
    return `${actor} proposed a new resource: ${row.new_value ?? '(unnamed)'}`;
  }
  if (row.event_kind === 'resource_approve') {
    const base = `${actor} approved a new resource: ${row.new_value ?? '(unnamed)'}`;
    return row.field_name === 'with_edits' ? `${base} (with edits)` : base;
  }

  // Phase 1.6 — user_tenant target microcopy (X.3.7 Part 3 access wording).
  if (row.target_type === 'user_tenant' && row.event_kind === 'direct_edit') {
    const grantee = row.target_display ?? actor;
    if (row.field_name === 'ai_cap') return aiCapSentence(row, actor, targetName);
    // Role-grant via pending_grants apply (auto-resolved at first sign-in)
    if (
      row.field_name === 'role' &&
      row.old_value === null &&
      row.queued_by_display_name !== null
    ) {
      return (
        <>
          {grantee} joined as {accessRoleLabel(row.new_value)}
          <span className="text-ink-soft text-xs ml-1">
            · queued by {row.queued_by_display_name}
          </span>
        </>
      );
    }
    // Direct grant by owner (no pending_grants origin)
    if (row.field_name === 'role' && row.old_value === null) {
      return `${actor} added ${targetName} as ${accessRoleLabel(row.new_value)}`;
    }
    // A re-invite taking effect at sign-in: the dormant membership is back.
    if (row.field_name === 'is_active' && row.new_value === 'true' && isSelfAuthored(row)) {
      return (
        <>
          {grantee} rejoined
          {row.queued_by_display_name !== null && (
            <span className="text-ink-soft text-xs ml-1">
              · queued by {row.queued_by_display_name}
            </span>
          )}
        </>
      );
    }
    // Role change
    if (row.field_name === 'role') {
      if (isSelfAuthored(row)) {
        return `${grantee}'s access changed from ${accessRoleLabel(row.old_value)} to ${accessRoleLabel(row.new_value)}`;
      }
      return `${actor} changed ${targetName}'s access from ${accessRoleLabel(row.old_value)} to ${accessRoleLabel(row.new_value)}`;
    }
    // Deactivate / reactivate
    if (row.field_name === 'is_active') {
      if (row.new_value === 'false') return `${actor} deactivated ${targetName}`;
      if (row.new_value === 'true')  return `${actor} reactivated ${targetName}`;
    }
  }

  // Note lifecycle on an org/individual (X.3.7 Part 3) — the note_id /
  // metadata never renders inline; it lives in the Details expander.
  if (row.event_kind === 'direct_edit' && row.field_name === 'note_deleted') {
    return `${actor} deleted a note on ${targetName}`;
  }
  if (row.event_kind === 'direct_edit' && row.field_name === 'note_edited') {
    return `${actor} edited a note on ${targetName}`;
  }
  if (row.event_kind === 'direct_edit' && row.field_name === 'merged_from') {
    return `${actor} merged another record into ${targetName}`;
  }
  // The split of a stored address into its parts is not anyone's edit of
  // the address: it reads as what happened, with the old and the new text
  // in Details.
  if (row.event_kind === 'direct_edit' && row.field_name === 'address_split') {
    return `${targetName}'s address was split into its parts`;
  }

  // §7.4 archive/restore special-case — direct_edit on is_active
  // (org/individual; user_tenant is_active handled above).
  if (row.event_kind === 'direct_edit' && row.field_name === 'is_active') {
    if (row.new_value === 'false') return `${actor} archived ${targetName}`;
    if (row.new_value === 'true') return `${actor} restored ${targetName}`;
  }

  if (row.event_kind === 'direct_edit') {
    return `${actor} edited ${targetName}'s ${fieldLabel(row)}`;
  }

  if (row.event_kind === 'applied_suggestion') {
    if (row.source_suggester_display_name) {
      return `${actor} approved ${row.source_suggester_display_name}'s suggested change to ${targetName}'s ${fieldLabel(row)}`;
    }
    return `${actor} approved a suggested change to ${targetName}'s ${fieldLabel(row)}`;
  }

  if (row.event_kind === 'note_create') {
    return `${actor} added a note on ${targetName}`;
  }

  if (row.event_kind === 'suggestion_submit') {
    return `${actor} suggested a change to ${targetName}'s ${fieldLabel(row)}`;
  }

  // Exhaustive over AdminTimelineEventKind; no "did something" fallback.
  // If a new kind is added without a branch, degrade to a readable
  // edit-shaped sentence rather than a system token.
  return `${actor} updated ${targetName}'s ${fieldLabel(row)}`;
}

function describeEventForAria(row: AdminTimelineRow, targetName: string): string {
  const body = renderEventBody(row, targetName);
  if (typeof body === 'string') return body;
  // The only non-string bodies are the queued role-grant and the rejoin;
  // build their sentences so screen readers hear the verb, not a generic
  // "Event on {target}".
  const grantee = row.target_display ?? targetName;
  const queued = row.queued_by_display_name !== null ? `, queued by ${row.queued_by_display_name}` : '';
  if (row.target_type === 'user_tenant' && row.field_name === 'role' && row.old_value === null) {
    return `${grantee} joined as ${accessRoleLabel(row.new_value)}${queued}`;
  }
  if (row.target_type === 'user_tenant' && row.field_name === 'is_active') {
    return `${grantee} rejoined${queued}`;
  }
  return `Event on ${targetName}`;
}

/**
 * Raw before→after detail string for the Details expander. Returns null
 * when there's nothing meaningful to show (note_create; archive/restore;
 * resource events; any row with no old/new value). For note_deleted /
 * note_edited the serialized metadata (old_value / new_value JSON) is
 * surfaced ONLY here, never in the headline.
 */
function rawDetail(row: AdminTimelineRow): string | null {
  // Notes-created carries no value delta worth showing.
  if (row.event_kind === 'note_create') return null;
  // Resource proposal/approval already name the resource in the headline.
  if (row.event_kind === 'resource_propose' || row.event_kind === 'resource_approve') {
    return null;
  }
  // Archive/restore (org/individual + user_tenant is_active) — nothing to show.
  if (row.field_name === 'is_active') return null;
  // A person's AI limit: the headline tells it, and its JSON never renders.
  if (row.field_name === 'ai_cap') return null;

  if (row.old_value === null && row.new_value === null) return null;

  // Note edit/delete metadata — show the raw serialized values verbatim
  // (the only place note_id / JSON is allowed to appear).
  if (row.field_name === 'note_deleted' || row.field_name === 'note_edited') {
    const parts: string[] = [];
    if (row.old_value !== null) parts.push(row.old_value);
    if (row.new_value !== null) parts.push(row.new_value);
    return parts.join('\n');
  }

  // Item 5 — junction fields carry JSON (an absolute slug array for an
  // applied edit, or a `{add,remove}` delta for a pending suggestion the
  // timeline UNION surfaces). The field-aware renderer turns either into a
  // readable string; no lookups loaded here, so slugs show as-is (never JSON).
  const valueOpts = { targetType: row.target_type, fieldName: row.field_name };
  const oldDisplay = truncate(humanizeFieldValue(row.old_value, valueOpts));
  const newDisplay = truncate(humanizeFieldValue(row.new_value, valueOpts));
  return `${oldDisplay} → ${newDisplay}`;
}

function truncate(s: string, max = 120): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function formatRelativeTime(iso: string | null): string {
  if (iso === null) return 'unknown time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
